/* eslint-disable prettier/prettier */
import { FunctionComponent, useRef, useState } from "react";
import Logo from "assets/adroit-logo.svg";
import { Menubar } from "primereact/menubar";
import { createSearchParams, useNavigate } from "react-router-dom";
import TokenService from "services/TokenService";
import Avatar from "assets/avatar.svg";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";

interface HeaderProps {}

const MERS_APPLICATION_IDS = [1, 9, 10];
const MERS_REGISTRATION_APPLICATION_ID = 9;
const INVESTOR_DELIVERY_APPLICATION_ID = 5;
const ADHOC_DELIVERY_APPLICATION_ID = 7;
const MORTGAGE_CLAS_VALIDATION_APPLICATION_ID = 8;

const Header: FunctionComponent<HeaderProps> = () => {
  const navigate = useNavigate();
  const menuRef = useRef<Menu>(null);
  const [searchText, setSearchText] = useState<string>("");

  const getUserType = TokenService.getLoggedInUserType();
  const getUserName = TokenService.getLoggedInUserName();
  const getUserMenu = TokenService.geUserMenu();

  let menuData: any = [];

  getUserMenu &&
    getUserMenu.length > 0 &&
    getUserMenu[0]?.applications.forEach((item: any) => {
      const items = [];

      // Investor Delivery (ApplicationId: 5)
      if (Number(item?.applicationId) === INVESTOR_DELIVERY_APPLICATION_ID) {
        if (item?.role === "CustomerAdmin") {
          items.push(
            {
              label: "Investor",
              icon: "pi pi-fw pi-briefcase",
              command: () => navigate(`investor`),
            },
            {
              label: "Package",
              icon: "pi pi-fw pi-box",
              command: () => navigate(`investor/package`),
            },
          );
        }

        items.push({
          label: "Manual Delivery",
          icon: "pi pi-fw pi-upload",
          command: () => navigate(`investor/manual-delivery`),
        });
      }

      // SnapDoc(1), Mers Registration(9), Mers Transfer(10) -> Investor Config (Customer Admin only)
      if (
        item?.role === "CustomerAdmin" &&
        MERS_APPLICATION_IDS.includes(Number(item?.applicationId))
      ) {
        items.push({
          label: "Investor Config",
          icon: "pi pi-fw pi-cog",
          command: () =>
            navigate(`investor-config-seetings/${item?.applicationId}`),
        });

        if (
          Number(item?.applicationId) === MERS_REGISTRATION_APPLICATION_ID
        ) {
          items.push({
            label: "Warehouse Bank",
            icon: "pi pi-fw pi-building",
            command: () => navigate("warehouse-bank"),
          });

          items.push({
            label: "Manage Investors",
            icon: "pi pi-fw pi-users",
            command: () => navigate("manage-investors"),
          });

          items.push({
            label: "Manage County",
            icon: "pi pi-fw pi-map-marker",
            command: () => navigate("manage-county"),
          });
        }
      }

      // Mortgage Clas Validation (ApplicationId: 8) -> Manage Settings (Customer Admin only)
      if (
        Number(item?.applicationId) ===
          MORTGAGE_CLAS_VALIDATION_APPLICATION_ID &&
        item?.role === "CustomerAdmin"
      ) {
        items.push({
          label: "Manage Settings",
          icon: "pi pi-fw pi-cog",
          command: () => navigate(`manage-settings`),
        });
      }

      // Adhoc Delivery (ApplicationId: 7) -> Jobs
      if (Number(item?.applicationId) === ADHOC_DELIVERY_APPLICATION_ID) {
        items.push({
          label: "Jobs",
          icon: "pi pi-fw pi-briefcase",
          command: () => navigate(`adhocJob/jobs`),
        });
      }

      // Add "Report"
      items.push({
        label: "Report",
        icon: "pi pi-fw pi-book",
        command: () => navigate(`reports/${item.applicationId}`),
      });

      if (items.length > 0) {
        menuData.push({
          label: item?.name,
          icon: "pi pi-fw pi-users",
          items,
        });
      }
    });

  const menuItems = getUserType
    ? [
        {
          label: "Customer",
          icon: "pi pi-fw pi-users",
          command: () => navigate("customers"),
        },
        {
          label: "Lookups",
          icon: "pi pi-fw pi-eye",
          items: [
            {
              label: "Applications",
              icon: "pi pi-fw pi-server",
              command: () => navigate("applications"),
            },
            {
              label: "External systems",
              icon: "pi pi-fw pi-cog",
              command: () => navigate("external-system"),
            },
            {
              label: "Users",
              icon: "pi pi-fw pi-users",
              command: () => navigate("users"),
            },
          ],
        },
        {
          label: "Invoices",
          icon: "pi pi-fw pi-file",
          items: [
            {
              label: "Investor Delivery Invoice",
              icon: "pi pi-fw pi-upload",
              command: () => navigate("invoices/investor-delivery-invoice"),
            },
            {
              label: "Mers Registration",
              icon: "pi pi-fw pi-cog",
              command: () => navigate("invoices/mers-invoice"),
            },
          ],
        },
        {
          label: "Report",
          icon: "pi pi-fw pi-book",
          command: () => navigate("reports"),
        },
      ]
    : menuData;

  const handleLogOut = () => {
    TokenService.removeUser();
    navigate("login");
  };

  const handleForgotPassword = () => {
    navigate("/change-password");
  };

  const searchReports = (e: any) => {
    setSearchText(e.target.value);
  };

  const redirectToReports = (event: any) => {
    if (event.key === "Enter" && searchText !== "") {
      navigate({
        pathname: "reports",
        search: createSearchParams({ searchQuery: searchText }).toString(),
      });
    }
  };

  const avatarMenuItems: MenuItem[] = [
    {
      label: "Change Password",
      icon: "pi pi-key",
      command: handleForgotPassword,
    },
    {
      label: "Logout",
      icon: "pi pi-sign-out",
      command: handleLogOut,
    },
  ];

  return (
    <>
      <div className="bg-white drop-shadow-md py-3 px-5 flex align-items-center relative z-10">
        <img
          src={Logo}
          alt="Adroit Logo"
          title="Adroit"
          style={{ width: 150 }}
          className="mr-6"
        />

        <div className="items-center justify-between flex flex-row w-full">
          <Menubar model={menuItems} />

          <div className="flex items-center">
            {/* 🔍 Search */}
            <span className="p-input-icon-left mr-4">
              <i className="pi pi-search" />
              <InputText
                value={searchText}
                onChange={searchReports}
                placeholder="Search Loan"
                onKeyDown={redirectToReports}
              />
            </span>

            {/* 👤 Avatar + Dropdown Menu */}
            <div className="flex items-center relative">
              <Menu model={avatarMenuItems} popup ref={menuRef} />
              <div
                className="flex items-center cursor-pointer"
                onClick={(e) => menuRef.current?.toggle(e)}
              >
                <img
                  src={Avatar}
                  alt="User Avatar"
                  className="mx-2"
                  style={{ width: 30 }}
                />
                <span className="text-sm">Hi, {getUserName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
